import { Component, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { NgxPermissionsService } from 'ngx-permissions';
import { AppConnectMessageType } from './core/enums/app-connect.enum';
import { CommonService } from './core/services/common.service';
import { PermissionsService } from './core/services/permissions.service';
import { UserService } from './core/services/user.service';
import { ApprovalRequestsApiActions } from './modules/main/states/approval-requests/approval-requests.actions';
import { ModuleListApiActions } from './modules/main/states/module-list/module-list.actions';
import { UserInfoActions } from './modules/main/states/users/users.actions';
import {
  APPROVAL_OTA_TAKE_ACTION_PERMISSION,
  APPROVAL_SOCIAL_PERMISSION,
  CENSOR_MANAGEMENT_PERMISSION,
  PERMISSION_CODE
} from './shared/const';

@Component({
  selector: 'soctrip-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  title = environment.APP_NAME;
  adminHubConnectUrl: string = environment.ADMIN_HUB_URL + '/app-connect';
  constructor(
    private store: Store,
    private router: Router,
    private userService: UserService,
    private ngxPermissionsService: NgxPermissionsService,
    private permissionsService: PermissionsService,
    private commonService: CommonService
  ) {
    this.connectAdminHub();
  }

  @HostListener('window:message', ['$event'])
  receiveMessage(event: any) {
    if (
      event.data.type === AppConnectMessageType.AUTHORIZED &&
      event.origin === environment.ADMIN_HUB_URL
    ) {
      if (!event.data || !event.data.token) {
        window.location.href = environment.ADMIN_HUB_URL;
        return;
      }
      this.save(event.data);
    }

    if (
      event.data.type === AppConnectMessageType.UNAUTHORIZED &&
      event.origin === environment.ADMIN_HUB_URL
    ) {
      window.location.href = environment.ADMIN_HUB_URL;
    }

    if (
      event.data.type === AppConnectMessageType.SUCCESSFUL &&
      event.origin === environment.ADMIN_HUB_URL
    ) {
      localStorage.clear();
      this.store.dispatch(UserInfoActions.onResetUserState());
      this.store.dispatch(ApprovalRequestsApiActions.onResetDataApprovalRequests());
      this.store.dispatch(ModuleListApiActions.onResetModuleList());
      const adminHubUrl = `${environment.ADMIN_HUB_URL}/login?returnUrl=${environment.APPROVAL_APP_URL}`;
      window.location.href = adminHubUrl;
    }

    if (
      [
        AppConnectMessageType.ADD_OWNER_USER_ID_SUCCESS,
        AppConnectMessageType.ADD_OWNER_USER_ID_FAIL
      ].includes(event.data.type) &&
      event.origin === environment.SOCIAL_APP_URL
    ) {
      this.store.dispatch(
        ApprovalRequestsApiActions.onSetLoadingForIframe({
          isIframeContentLoading: false
        })
      );
    }
  }

  save(data: any): void {
    localStorage.setItem(environment.TOKEN, JSON.stringify(data.token));
    localStorage.setItem(environment.USER_PROFILE, data.profile);
    localStorage.setItem(environment.AUTH_STATUS, JSON.stringify({ isLoggedIn: true }));

    this.userService.getPermissionCodeList().subscribe(res => {
      if (res.data?.length) {
        this.permissionsService.setEncryptedCache(res.data);
        this.ngxPermissionsService.loadPermissions(res.data);

        const permission = this.permissionsService.getEncryptedCache();

        const { approvalAccess, approvalApprove } = APPROVAL_SOCIAL_PERMISSION;

        const { censorAccess } = CENSOR_MANAGEMENT_PERMISSION;

        const {
          approvalOtaApprove,
          approvalOtaBlock,
          approvalOtaDelete,
          approvalOtaKeep,
          approvalOtaReject,
          approvalOtaWarning
        } = APPROVAL_OTA_TAKE_ACTION_PERMISSION;

        const hasApprovalPermission = this.commonService.hasAnyPermission(permission, [
          approvalAccess,
          approvalApprove,
          PERMISSION_CODE.superAdmin
        ]);

        const hasCensorPermission = this.commonService.hasAnyPermission(permission, [
          censorAccess,
          PERMISSION_CODE.superAdmin
        ]);

        const hasOtaPermission = this.commonService.hasAnyPermission(permission, [
          approvalOtaApprove,
          approvalOtaBlock,
          approvalOtaDelete,
          approvalOtaKeep,
          approvalOtaReject,
          approvalOtaWarning,
          PERMISSION_CODE.superAdmin
        ]);

        if (window.location.pathname === '/') {
          if (hasApprovalPermission) {
            this.router.navigate(['/approval-requests']);
            return;
          }

          if (hasCensorPermission) {
            this.router.navigate(['/censor-management']);
            return;
          }

          if (hasOtaPermission) {
            this.router.navigate(['/ota']);
            return;
          }

          window.location.href = environment.ADMIN_HUB_URL;
        }
      }
    });
  }

  connectAdminHub() {
    const ifr = document.createElement('iframe');
    ifr.classList.add('hidden');
    document.body.appendChild(ifr);
    ifr.src = this.adminHubConnectUrl;
    const connect = () => {
      const result = {
        type: AppConnectMessageType.CONNECT
      };
      ifr.contentWindow?.postMessage(result, { targetOrigin: environment.ADMIN_HUB_URL });
    };
    ifr.onload = connect;
  }
}
